// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@keller/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@keller/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@keller/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@keller/App/Components/Notify/Notify";
import { CustomAppState } from "@keller/App/Store/Reducers";
import {
    getIsNonPurchasable,
    xIsAuthenticated,
    xisCheckoutDisabled,
} from "@keller/App/Store/Selectors/GeneralSelectors";
import { color, styledScrollbar, xScreen } from "@keller/XDL/Utilities";
import { xCanCheckoutWithCart } from "@keller/XDL/Utilities/checkout.help";
import { mergeWithCss } from "@keller/XDL/Utilities/mergeWithCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const isCheckoutDisabled = xisCheckoutDisabled(cart);
    const currentPage = getCurrentPage(state);

    return {
        isCheckoutDisabled,
        canSubmitRequisition: canSubmitRequisition(cart),
        canCheckoutWithCart: xCanCheckoutWithCart(state, cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        shouldLoadCart:
            !getCurrentCartState(state).value &&
            !getCurrentCartState(state).isLoading &&
            currentPage.type !== "CartPage",
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
        isNonPurchasable: getIsNonPurchasable(state),
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    wrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    notifyWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    totalItems?: TypographyProps;
    noCartLinesNotify?: TypographyProps;
    viewCartLink?: LinkPresentationProps;
    cartLinesContainer?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    brandName?: TypographyProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    actionWrapper?: InjectableCss;
    subTotalWrapper?: InjectableCss;
    subTotalTitle?: TypographyProps;
    subTotalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
}

const miniCartStyles: MiniCartStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: 470px;
            min-width: 250px;
            ${xScreen("lte", "md")} {
                width: 100%;
                max-width: initial;
                justify-content: space-between;
            }
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 470px;
            min-width: 250px;
            min-height: 250px;
            ${xScreen("lte", "md")} {
                max-width: initial;
                min-width: initial;
                width: 100%;
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    notifyWrapper: {
        css: css`
            padding: 20px 14px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            border-top: 1px solid ${color("common.border")};
            padding: 22px 20px 20px;
            ${xScreen("lte", "md")} {
                border-top: 0;
                padding-top: 20px;
                padding-bottom: 16px;
            }
        `,
    },
    totalItems: {
        size: 15,
        weight: "normal",
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            text-align: right;
            margin-bottom: 0;
            display: flex;
            justify-content: space-between;
            width: 100%;
            .divider {
                margin: 0 10px;
            }
        `,
    },
    viewCartLink: {
        color: "secondary",
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    cartLinesContainer: {
        css: css`
            padding-top: 20px;
            padding-right: 12px;
            ${xScreen("lte", "md")} {
                border-top: 1px solid ${color("common.border")};
            }
            ${xScreen("lte", 767)} {
                border-top: none;
            }
        `,
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            min-height: 150px;
            max-height: 370px;
            & > div:last-child {
                margin-bottom: 10px;
            }
            overflow-x: hidden;
            ${styledScrollbar()}

            ${xScreen("lte", "md")} {
                margin-bottom: 5px;
                padding-right: 0;
                max-height: initial;
                flex-grow: 1;
                display: flex;
                gap: 20px;
                flex-wrap: wrap;
                justify-content: space-between;
                & > div:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            gap: 0,
            css: css`
                width: auto;
                flex: 1 0 auto;
            `,
        },
    },
    brandName: {
        size: 12,
        weight: "bold",
        as: "p",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 5px;
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            position: relative;
            padding: 0 20px 20px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    subTotalTitle: {
        size: 15,
        css: css`
            margin-right: 15px;
            letter-spacing: 0;
            line-height: 26px;
        `,
    },
    subTotalValue: {
        size: 26,
        weight: 900,
        css: css`
            letter-spacing: 0;
            line-height: 32px;
        `,
    },
    subTotalWrapper: {
        css: css`
            margin-bottom: 22px;
        `,
    },
    checkoutButton: {
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
    viewCartButton: {
        variant: "secondary",
        sizeVariant: "large",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
    noCartLinesNotify: {
        css: css`
            max-width: 100%;
        `,
    },
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    canSubmitRequisition,
    doPunchOutCheckout,
    isUpdatingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    isPopupShown,
    canCheckoutWithCart,
    isAuthenticated,
    isNonPurchasable,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        setIsCheckoutLoading(true);
        preloadCheckoutShippingData({
            onSuccess: () => {
                setIsCheckoutLoading(false);
                history.push(checkoutShippingPageUrl!);
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled = isCheckoutDisabled || isCheckoutLoading || isLoadingCart;

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...mergeWithCss(styles.wrapper, styles.notifyWrapper)} className="empty-cartline">
                <Notify {...styles.noCartLinesNotify} type="info">
                    {isAuthenticated && isNonPurchasable
                        ? siteMessage("Keller_Cart_NoOrderLines_WebOnly")
                        : siteMessage("Cart_NoOrderLines")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoadingCart || isUpdatingCartLine}>
            <StyledWrapper {...styles.cartLinesContainer}>
                <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                    <StyledWrapper
                        {...styles.cartLinesWrapper}
                        ref={parentBoxRef}
                        data-test-selector="cartLines-container"
                    >
                        {cartLines?.map(cartLine => {
                            const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                            return (
                                <MiniCartLineCard
                                    key={`miniCartLine_${cartLine.id}`}
                                    cartLine={cartLine}
                                    cart={cart}
                                    qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                    removeCartLineClickHandler={removeCartLineClickHandler}
                                    errorMessages={errorMessages}
                                />
                            );
                        })}
                    </StyledWrapper>
                </FancyScroll>
            </StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.totalItems}>
                        <Typography>
                            {totalCountDisplay}&nbsp;
                            {translate(`Item${totalCountDisplay > 2 || totalCountDisplay === 0 ? "s" : ""}`)}
                        </Typography>
                        <StyledWrapper>
                            <Typography
                                size={12}
                                css={css`
                                    margin-right: 12px;
                                `}
                            >{`${translate("subtotal")}:`}</Typography>
                            <Typography size={20} weight="bold">
                                {orderSubTotalDisplay}
                            </Typography>
                        </StyledWrapper>
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.actionWrapper}>
                    <Button {...styles.viewCartButton} onClick={handleViewCarClick}>
                        {translate("View Cart")}
                    </Button>
                    {canCheckoutWithCart && (
                        <>
                            {!canSubmitRequisition ? (
                                <Button
                                    {...styles.checkoutButton}
                                    onClick={checkoutHandler}
                                    disabled={isCheckoutButtonDisabled}
                                >
                                    {cart?.requiresApproval
                                        ? translate("Checkout for Approval")
                                        : isPunchOutOrder
                                          ? translate("Return to Procurement System")
                                          : translate("Continue to Checkout")}
                                </Button>
                            ) : (
                                <Button
                                    {...styles.checkoutButton}
                                    onClick={submitRequisitionClickHandler}
                                    data-test-selector="cartTotal_SubmitRequisition"
                                >
                                    {translate("Submit Requisition")}
                                </Button>
                            )}
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
